import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, ChevronDown, ChevronRight, Search, Pencil, Trash2 } from 'lucide-react'
import { Modal, ConfirmModal } from '../../common/Modal/Modal'
import { Pagination } from '../../common/Select/Select'
import {
  fetchFaqs,
  createFaq,
  updateFaq,
  deleteFaq,
  clearFaqError,
} from './services/faqSlice'

const EMPTY_FORM = { category: '', question: '', answer: '', sort_order: '', status: 1 }

export default function FAQs() {
  const dispatch = useDispatch()
  const { list: faqs, isLoading, isSaving, isError, message, errors } =
    useSelector((state) => state.faq)

  const [open, setOpen] = useState(null)
  const [search, setSearch] = useState('')
  const [catFilter, setCatFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  useEffect(() => { dispatch(fetchFaqs()) }, [dispatch])

  const categories = useMemo(() => [...new Set(faqs.map((f) => f.category))], [faqs])

  const filtered = faqs.filter((f) => {
    const q = search.toLowerCase()
    const matchSearch = !q || f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q) || f.category.toLowerCase().includes(q)
    const matchCat = catFilter === 'all' || f.category === catFilter
    return matchSearch && matchCat
  })

  /* pagination */
  useEffect(() => { setPage(1) }, [search, catFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const handlePageSizeChange = (size) => { setPageSize(size); setPage(1) }

  const fieldError = (name) =>
    errors?.[name] ? <small style={{ color: '#ef4444' }}>{errors[name][0]}</small> : null

  const openAdd = () => {
    dispatch(clearFaqError())
    setActive(null)
    setForm({ ...EMPTY_FORM, category: catFilter !== 'all' ? catFilter : '' })
    setModalOpen(true)
  }

  const openEdit = (faq) => {
    dispatch(clearFaqError())
    setActive(faq)
    setForm({
      category: faq.category,
      question: faq.question,
      answer: faq.answer,
      sort_order: faq.sort_order,
      status: faq.status,
    })
    setModalOpen(true)
  }

  const save = async () => {
    if (!form.category.trim() || !form.question.trim() || !form.answer.trim()) return
    const payload = {
      ...form,
      sort_order: form.sort_order === '' ? undefined : Number(form.sort_order),
      status: Number(form.status),
    }
    const result = active
      ? await dispatch(updateFaq({ id: active.id, ...payload }))
      : await dispatch(createFaq(payload))
    if (result.meta.requestStatus === 'fulfilled') setModalOpen(false)
  }

  const onDelete = async () => {
    const result = await dispatch(deleteFaq(active.id))
    if (result.meta.requestStatus === 'fulfilled') {
      setDeleteOpen(false)
      setActive(null)
    }
  }

  return (
    <div className="dash-page page-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
        <div>
          <h1 className="page-title">FAQs</h1>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add FAQ</button>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={13} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
          <input className="input" style={{ paddingLeft: 36 }} placeholder="Search FAQs…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="view-tabs" style={{ margin: 0, flexWrap: 'wrap' }}>
          {['all', ...categories].map((c) => (
            <button key={c} className={`vtab${catFilter === c ? ' active' : ''}`} onClick={() => setCatFilter(c)}>
              {c === 'all' ? 'All' : c}
            </button>
          ))}
        </div>
      </div>

      {isError && message && !modalOpen && <div className="login-error">{message}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
        {isLoading && <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>}
        {!isLoading && filtered.length === 0 && (
          <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text3)' }}>No FAQs found</div>
        )}

        {paginated.map((faq) => (
          <div key={faq.id} className="card" style={{ padding: 0, cursor: 'pointer', opacity: faq.status === 1 ? 1 : 0.65 }}
            onClick={() => setOpen((o) => (o === faq.id ? null : faq.id))}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-md) var(--space-lg)', gap: 'var(--space-md)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', minWidth: 0 }}>
                <span className="badge badge-gold" style={{ flexShrink: 0 }}>{faq.category}</span>
                {faq.status !== 1 && <span className="badge badge-inactive" style={{ flexShrink: 0 }}>hidden</span>}
                <span style={{ fontSize: 'var(--fs-md)', fontWeight: 500, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{faq.question}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button className="btn btn-ghost btn-icon" onClick={(e) => { e.stopPropagation(); openEdit(faq) }} aria-label="Edit FAQ"><Pencil size={14} /></button>
                <button className="btn btn-ghost btn-icon" onClick={(e) => { e.stopPropagation(); setActive(faq); setDeleteOpen(true) }} aria-label="Delete FAQ"><Trash2 size={14} /></button>
                {open === faq.id
                  ? <ChevronDown size={16} style={{ color: 'var(--accent-hover)', flexShrink: 0 }} />
                  : <ChevronRight size={16} style={{ color: 'var(--text3)', flexShrink: 0 }} />}
              </div>
            </div>
            {open === faq.id && (
              <div style={{ padding: 'var(--space-md) var(--space-lg)', fontSize: 'var(--fs-base)', color: 'var(--text2)', lineHeight: 1.7, borderTop: '1px solid var(--border-subtle)', whiteSpace: 'pre-wrap' }}>
                {faq.answer}
              </div>
            )}
          </div>
        ))}

        {!isLoading && filtered.length > 0 && (
          <div className="card" style={{ padding: 0 }}>
            <Pagination
              total={filtered.length}
              page={currentPage}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 20, 50, 100]}
            />
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        title={active ? 'Edit FAQ' : 'Add FAQ'}
        subtitle="Shown on the website FAQs page"
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving…' : active ? 'Update FAQ' : 'Create FAQ'}
            </button>
          </>
        )}
      >
        {isError && message && <div className="login-error" style={{ marginBottom: 12 }}>{message}</div>}

        <div className="form-grid">
          <div className="form-field">
            <label>Category <small style={{ color: 'var(--text3)' }}>(same name = same group on website)</small></label>
            <input className="input" list="faq-cats" placeholder="e.g. Returns & refunds" value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <datalist id="faq-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            {fieldError('category')}
          </div>
          <div className="form-field">
            <label>Question</label>
            <input className="input" placeholder="Type FAQ question" value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })} />
            {fieldError('question')}
          </div>
          <div className="form-field">
            <label>Answer</label>
            <textarea className="input" rows={5} placeholder="Type answer" value={form.answer}
              onChange={(e) => setForm({ ...form, answer: e.target.value })} style={{ resize: 'vertical' }} />
            <small style={{ color: 'var(--text3)' }}>
              Link: [Return Policy](/return-refund-policy) · Bold: **text** · Navi line = line break
            </small>
            {fieldError('answer')}
          </div>
          <div className="form-field">
            <label>Sort Order <small style={{ color: 'var(--text3)' }}>(nana number pehla; khali = chhelle)</small></label>
            <input type="number" min={0} className="input" value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
            {fieldError('sort_order')}
          </div>
          <div className="form-field">
            <label>Status</label>
            <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}>
              <option value={1}>Active (show on website)</option>
              <option value={0}>Hidden</option>
            </select>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete FAQ"
        message={`Delete "${active?.question || 'selected FAQ'}"?`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page{display:flex;flex-direction:column;gap:var(--space-lg);width:100%;}
        .login-error{background:rgba(239,68,68,.12);border:1px solid rgba(239,68,68,.35);color:#fca5a5;font-size:.875rem;padding:.6rem .9rem;border-radius:10px;}
      `}</style>
    </div>
  )
}